import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Table,
  SimpleButton,
  LoadingButton,
  FormDialog,
  Input,
  useStateContext,
  DropdownMenu,
  Modal,
  Toast,
} from "@veda/dashboard-ui";
import { BsTrash, BsBoxArrowUpRight } from "react-icons/bs";
import { MdAdd } from "react-icons/md";
import { useProcedimenti } from "../hooks/useProcedimento";
import { usePppApi } from "../services/pppApi";
import { useAuthUser } from "../hooks/useAuthUser";
import { usePppAiContext } from "../hooks/usePppAiContext";

const STATO_KEYS = {
  bozza: { key: "ppp_stato_bozza", class: "bg-gray-100 text-gray-700" },
  avviato: { key: "ppp_stato_avviato", class: "bg-blue-100 text-blue-700" },
  sospeso: { key: "ppp_stato_sospeso", class: "bg-yellow-100 text-yellow-700" },
  concluso: { key: "ppp_stato_concluso", class: "bg-green-100 text-green-700" },
  archiviato: {
    key: "ppp_stato_archiviato",
    class: "bg-gray-200 text-gray-500",
  },
};

function StatoBadge({ stato, _locate }) {
  const cfg = STATO_KEYS[stato] ?? { key: stato, class: "" };
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-medium ${cfg.class}`}>
      {_locate(cfg.key) ?? stato}
    </span>
  );
}

export default function ProcedimentiList() {
  const navigate = useNavigate();
  const { _locate, setOpenForm, addModal, addToast } = useStateContext();
  const { procedimenti, loading, refresh } = useProcedimenti();
  const api = usePppApi();
  const { can } = useAuthUser();
  usePppAiContext('ppp.procedimento');

  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ titolo: "" });

  const handleElimina = (row) => {
    addModal(
      <Modal
        title={`${_locate("ppp_elimina_titolo")}: ${row.titolo}`}
        content={_locate("ppp_elimina_messaggio")}
        confirm={_locate("ppp_elimina")}
        style="danger"
        onClick={() => {
          api.eliminaProcedimento(row.id, {
            onSuccess: () => {
              addToast(<Toast duration={5} content={_locate("ppp_procedimento_eliminato")} />);
              refresh();
            },
          });
        }}
      />
    );
  };

  const cols = [
    { name: "action",  label: "",                        size: "w-10 max-w-[40px]", orderable: 0 },
    { name: "titolo",  label: _locate("ppp_col_titolo") },
    { name: "rupNome", label: _locate("ppp_col_rup"),   size: "w-40" },
    { name: "stato",   label: _locate("ppp_col_stato"), size: "w-28" },
  ];

  const cells_style = {
    action: (row) => (
      <DropdownMenu
        menu_items={[
          <button
            key="apri"
            onClick={() => navigate(`/ppp/${row.id}`)}
            className="w-full flex items-center gap-4 px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-600 dark:hover:text-white"
          >
            <BsBoxArrowUpRight />
            <span>{_locate("ppp_apri")}</span>
          </button>,
          can("ppp.delete") && (
            <button
              key="elimina"
              onClick={() => handleElimina(row)}
              className="w-full flex items-center gap-4 px-4 py-2 text-red-600 hover:bg-gray-100 dark:hover:bg-gray-600"
            >
              <BsTrash />
              <span>{_locate("ppp_elimina")}</span>
            </button>
          ),
        ].filter(Boolean)}
      />
    ),
    titolo: (row) => (
      <div
        className="cursor-pointer"
        onClick={() => navigate(`/ppp/${row.id}`)}
      >
        <div className="text-xs font-bold text-[#555454] dark:text-dark-text-normal hover:text-gray-400 dark:hover:text-[#a9a68e]">
          {row.titolo}
        </div>
        <div className="text-xs text-[#828c95] mt-0.5">
          {row.enteConcedente}
          {row.enteConcedente && (row.faseAttiva || _locate("ppp_non_avviato")) ? " · " : ""}
          {row.faseAttiva
            ? `${_locate("ppp_tab_fasi")} ${row.faseAttiva}`
            : _locate("ppp_non_avviato")}
        </div>
      </div>
    ),
    rupNome: (row) => <span className="text-xs">{row.rupNome ?? "—"}</span>,
    stato:   (row) => <StatoBadge stato={row.stato} _locate={_locate} />,
  };

  const handleCrea = () => {
    setSaving(true);
    api.creaProcedimento(form, {
      onSuccess: (data) => {
        setSaving(false);
        setOpenForm(false);
        refresh();
        navigate(`/ppp/${data.id}`);
      },
      onError: () => setSaving(false),
    });
  };

  return (
    <div className="p-6">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-semibold text-gray-900">
          {_locate("ppp_lista_titolo")}
        </h1>
        {can('ppp.create') && (
          <SimpleButton customFunc={() => setOpenForm(true)} text={<span className="flex items-center gap-1"><MdAdd size={16} />{_locate("ppp_nuovo_procedimento")}</span>} />
        )}
      </div>

      <Table
        id="ppp-procedimenti"
        dataset={procedimenti}
        cols={cols}
        cells_style={cells_style}
        loading={loading}
      />
      </div>

      <FormDialog title={_locate("ppp_nuovo_titolo")} size="w-[560px]">
        <div className="space-y-6">
          <p className="text-sm text-gray-500">{_locate("ppp_form_descrizione") ?? "Inserisci il titolo per avviare il nuovo procedimento."}</p>
          <Input
            label={_locate("ppp_form_titolo")}
            required
            value={form.titolo}
            onChange={(e) => setForm((f) => ({ ...f, titolo: e.target.value }))}
          />
          <p className="text-xs text-gray-400">
            {_locate("ppp_form_rup_dopo") ??
              "Il RUP potrà essere invitato e assegnato dopo la creazione, dal dettaglio del procedimento."}
          </p>
          <div className="flex gap-2 justify-end pt-2 border-t border-gray-100">
            <SimpleButton
              customFunc={() => setOpenForm(false)}
              text={_locate("ppp_form_annulla")}
            />
            <LoadingButton
              isLoading={saving}
              customFunc={form.titolo ? handleCrea : undefined}
              text={_locate("ppp_form_crea")}
            />
          </div>
        </div>
      </FormDialog>
    </div>
  );
}
